/**
 * CLIENT MEETING STATUS CARD
 *
 * Displays the client meeting status and allows IT team to mark it as completed.
 * This is a prominent, standalone card for better visibility.
 */

'use client';

import { useState } from 'react';
import { createClient } from '@/lib/supabase';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, CheckCircle2, Clock } from 'lucide-react';
import { markClientMeetingDone } from '@/lib/onboarding-status';
import type { Location, Task } from '@/types/database';

interface ClientMeetingStatusCardProps {
  location: Location | null;
  task: Task;
  userId: string;
  onStatusChange?: (newStatus: 'PENDING' | 'COMPLETED') => void;
}

export function ClientMeetingStatusCard({
  location,
  task,
  userId,
  onStatusChange,
}: ClientMeetingStatusCardProps) {
  const supabase = createClient();
  const [clientMeetingStatus, setClientMeetingStatus] = useState<'PENDING' | 'COMPLETED'>(
    (location as any)?.client_meeting_status || 'PENDING'
  );
  const [clientMeetingLoading, setClientMeetingLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCompleted = task.status === 'COMPLETED';

  const handleClientMeetingToggle = async () => {
    try {
      setClientMeetingLoading(true);
      setError(null);

      // Update this app's database - mark ALL locations for this organization as completed
      const { error: locationError } = await supabase
        .from('locations')
        .update({
          client_meeting_status: 'COMPLETED',
        })
        .eq('organization_id', task.organization_id);

      if (locationError) throw locationError;

      // Call edge function in main app to mark meeting as done
      await markClientMeetingDone(location?.id!, true);

      // Log activity
      await supabase.rpc('log_activity', {
        p_user_id: userId,
        p_action: 'TASK_UPDATED' as any,
        p_description: `Client meeting completed for ${location?.name}`,
        p_organization_id: task.organization_id,
        p_task_id: task.id,
        p_location_id: location?.id,
      });

      const newStatus = 'COMPLETED' as const;
      setClientMeetingStatus(newStatus);
      onStatusChange?.(newStatus);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update meeting status');
    } finally {
      setClientMeetingLoading(false);
    }
  };

  return (
    <Card className="mb-6 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
      <CardContent className="pt-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
              {clientMeetingStatus === 'COMPLETED' ? (
                <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
              ) : (
                <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              )}
              <label className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                Meeting with Client
                <span className="text-[10px] font-normal text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/50 px-2 py-0.5 rounded-full border border-blue-200 dark:border-blue-800">
                  All Locations
                </span>
              </label>
            </div>
          <button
            type="button"
            onClick={handleClientMeetingToggle}
            disabled={clientMeetingLoading || isCompleted || clientMeetingStatus === 'COMPLETED'}
            className="px-4 py-2 rounded-lg font-medium text-sm transition-all bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-600 disabled:opacity-50"
          >
            {clientMeetingLoading ? (
              <>
                <Loader2 className="inline mr-2 h-4 w-4 animate-spin" />
                Marking...
              </>
            ) : clientMeetingStatus === 'COMPLETED' ? (
              'Done'
            ) : (
              'Mark as Done'
            )}
          </button>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-3">
          {clientMeetingStatus === 'COMPLETED'
            ? '✓ Client meeting has been completed for all locations in this organization.'
            : 'This action applies to all locations. Mark this when the single client meeting for the organization is complete.'}
        </p>
        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 p-3 rounded-md mt-3">
            {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
